<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page customer-sales-summary-page">
      <section class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="8" :sm="24">
              <a-form-item label="日期">
                <a-range-picker v-model="dateRange" style="width: 100%" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="客商">
                <a-input v-model="query.customerKeyword" allow-clear placeholder="客商编号或名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="供应商">
                <a-select
                  v-model="query.supplierId"
                  allow-clear
                  show-search
                  option-filter-prop="children"
                  placeholder="全部供应商">
                  <a-select-option v-for="item in supplierOptions" :key="item.id" :value="item.id">
                    {{ item.supplierCode }} · {{ item.supplierName }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="分类">
                <a-tree-select
                  v-model="query.categoryId"
                  allow-clear
                  show-search
                  tree-node-filter-prop="title"
                  :dropdown-style="{ maxHeight: '360px', overflow: 'auto' }"
                  placeholder="全部分类"
                  :tree-data="categoryTreeSelect"
                  style="width: 100%"
                />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="规格">
                <a-input v-model="query.specKeyword" allow-clear placeholder="SKU编码或规格" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="单位">
                <a-select v-model="query.unitType" @change="onUnitTypeChange">
                  <a-select-option value="business">销售单位</a-select-option>
                  <a-select-option value="base">基本单位</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">查询</a-button>
              <a-button icon="redo" @click="resetQuery">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>客商销售汇总表</h3>
            <p>按客商 + SKU + 销售单位汇总已审核销售出库；数量按折让金额同比例折算（出库−折让）；销售金额为价税合计减已审核销售折让；成本取自出库行 cost_amount。</p>
          </div>
          <div class="erp-toolbar-actions">
            <span v-if="pagination.total" class="customer-sales-summary-totals">
              合计销售 <strong class="erp-money">¥{{ formatMoney(amountTotal) }}</strong>
              成本 <strong class="erp-money">¥{{ formatMoney(costTotal) }}</strong>
              毛利 <strong class="erp-money">¥{{ formatMoney(grossProfitTotal) }}</strong>
            </span>
            <a-button
              v-hasPermi="['erp:report:customer-sales-summary:export']"
              icon="download"
              :loading="exporting"
              :disabled="!pagination.total"
              @click="doExport">导出</a-button>
          </div>
        </div>
        <a-table
          :key="tableRenderKey"
          row-key="id"
          bordered
          size="middle"
          :columns="tableColumns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :scroll="tableScroll"
          :locale="{ emptyText: '暂无汇总数据' }"
          @change="onPageChange">
          <template slot="text" slot-scope="value">
            <span :title="displayValue(value)">{{ displayValue(value) }}</span>
          </template>
          <template slot="qty" slot-scope="text">{{ formatQty(text) }}</template>
          <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
          <template slot="rate" slot-scope="value">{{ formatRate(value) }}</template>
        </a-table>
      </section>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import {
  pageCustomerSalesSummaryReport,
  listCustomerSalesSummarySupplierOptions,
  exportCustomerSalesSummaryReport
} from '@/api/erp/customerSalesSummaryReport'
import { treeProductCategory } from '@/api/erp/productCategory'

const emptyQuery = () => ({
  supplierId: undefined,
  categoryId: undefined,
  customerKeyword: '',
  specKeyword: '',
  unitType: 'business'
})

export default {
  name: 'ErpCustomerSalesSummaryReport',
  data () {
    return {
      query: emptyQuery(),
      dateRange: [],
      supplierOptions: [],
      categoryTreeRaw: [],
      rows: [],
      amountTotal: 0,
      costTotal: 0,
      grossProfitTotal: 0,
      loading: false,
      exporting: false,
      tableRenderKey: 0,
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showTotal: total => `共 ${total} 条` }
    }
  },
  computed: {
    categoryTreeSelect () {
      return this.toTreeSelectData(this.categoryTreeRaw)
    },
    tableScroll () {
      const w = this.tableColumns.reduce((sum, col) => sum + (col.width || 0), 0)
      return w > 0 ? { x: w } : undefined
    },
    tableColumns () {
      const isBase = this.query.unitType === 'base'
      const unitLabel = isBase ? '基本单位' : '销售单位'
      return [
        { title: '客商编码', dataIndex: 'customerCode', width: 120, ellipsis: true, scopedSlots: { customRender: 'text' } },
        { title: '客商名称', dataIndex: 'customerName', width: 140, scopedSlots: { customRender: 'text' } },
        { title: '二级分类', dataIndex: 'categoryName', width: 110, scopedSlots: { customRender: 'text' } },
        { title: 'SKU编码', dataIndex: 'skuCode', width: 120, ellipsis: true, scopedSlots: { customRender: 'text' } },
        { title: 'SKU名称', dataIndex: 'skuName', width: 140, scopedSlots: { customRender: 'text' } },
        { title: '规格名称', dataIndex: 'specName', width: 110, scopedSlots: { customRender: 'text' } },
        { title: '数量（出库-折让）', dataIndex: 'displayQty', width: 130, scopedSlots: { customRender: 'qty' } },
        { title: '单位', dataIndex: 'displayUnitName', width: 88, scopedSlots: { customRender: 'text' } },
        { title: '平均单价', dataIndex: 'displayUnitPrice', width: 108, scopedSlots: { customRender: 'money' } },
        { title: '合计销售金额', dataIndex: 'displayAmount', width: 120, scopedSlots: { customRender: 'money' } },
        { title: '销售成本', dataIndex: 'displaySalesCost', width: 108, scopedSlots: { customRender: 'money' } },
        { title: '毛利额', dataIndex: 'displayGrossProfit', width: 100, scopedSlots: { customRender: 'money' } },
        { title: '毛利率(%)', dataIndex: 'displayGrossProfitRate', width: 100, scopedSlots: { customRender: 'rate' } }
      ]
    }
  },
  created () {
    this.loadSupplierOptions()
    treeProductCategory().then(res => {
      this.categoryTreeRaw = this.normalizeTree((res && res.data) || [])
    }).catch(() => { this.categoryTreeRaw = [] })
    this.load()
  },
  methods: {
    normalizeTree (nodes) {
      return (nodes || []).map(n => ({
        id: n.id,
        categoryName: n.categoryName || n.name,
        children: this.normalizeTree(n.children || [])
      }))
    },
    toTreeSelectData (nodes) {
      return (nodes || []).map(n => ({
        title: n.categoryName,
        value: n.id,
        key: String(n.id),
        children: n.children && n.children.length ? this.toTreeSelectData(n.children) : undefined
      }))
    },
    displayValue (value) {
      return value === null || value === undefined || value === '' ? '—' : value
    },
    formatQty (value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (Number.isNaN(n)) return '—'
      const s = n.toFixed(6)
      return s.replace(/\.?0+$/, '') || '0'
    },
    formatMoney (value) {
      return Number(value || 0).toFixed(2)
    },
    formatRate (value) {
      if (value === null || value === undefined || value === '') return '—'
      return Number(value).toFixed(2)
    },
    queryParams () {
      const specKeyword = (this.query.specKeyword || '').trim()
      return {
        supplierId: this.query.supplierId,
        categoryId: this.query.categoryId,
        customerKeyword: (this.query.customerKeyword || '').trim() || undefined,
        specKeyword: specKeyword || undefined,
        dateStart: this.dateRange[0] ? this.dateRange[0].format('YYYY-MM-DD') : undefined,
        dateEnd: this.dateRange[1] ? this.dateRange[1].format('YYYY-MM-DD') : undefined,
        unitType: this.query.unitType || 'business',
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
    },
    loadSupplierOptions () {
      listCustomerSalesSummarySupplierOptions().then(res => {
        this.supplierOptions = res.data || []
      }).catch(() => { this.supplierOptions = [] })
    },
    load () {
      this.loading = true
      pageCustomerSalesSummaryReport(this.queryParams()).then(res => {
        const data = res.data || {}
        this.rows = (data.records || []).map(r => ({ ...r }))
        this.tableRenderKey += 1
        this.pagination.total = Number(data.total || 0)
        this.amountTotal = Number(data.amountTotal || 0)
        this.costTotal = Number(data.costTotal || 0)
        this.grossProfitTotal = Number(data.grossProfitTotal || 0)
      }).catch(() => {
        this.rows = []
        this.pagination.total = 0
        this.amountTotal = 0
        this.costTotal = 0
        this.grossProfitTotal = 0
      }).finally(() => { this.loading = false })
    },
    search () {
      this.pagination.current = 1
      this.load()
    },
    onUnitTypeChange (val) {
      this.query.unitType = val || 'business'
      this.search()
    },
    resetQuery () {
      this.query = emptyQuery()
      this.dateRange = []
      this.pagination.current = 1
      this.load()
    },
    onPageChange (pag) {
      this.pagination.current = pag.current
      this.pagination.pageSize = pag.pageSize
      this.load()
    },
    async doExport () {
      this.exporting = true
      try {
        const params = { ...this.queryParams() }
        delete params.pageNum
        delete params.pageSize
        const res = await exportCustomerSalesSummaryReport(params)
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `客商销售汇总_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.customer-sales-summary-totals {
  margin-right: 12px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
}
.customer-sales-summary-totals strong {
  margin: 0 8px 0 4px;
}
</style>
